// Maps the modal's internal tag slugs to the store's live metafield filter
// facets (custom.filter_room / filter_size / filter_color). The storefront
// collection filters are metafield-based, so a plain `filter.p.tag=` param is
// silently ignored and returns the unfiltered grid — these facet params are
// what actually filter. Keep values matching the metafield option labels exactly.
const RUG_FINDER_FACETS = {
  'room-living-room': ['filter.p.m.custom.filter_room', 'Living Room'],
  'room-bedroom': ['filter.p.m.custom.filter_room', 'Bedroom'],
  'room-kitchen': ['filter.p.m.custom.filter_room', 'Kitchen'],
  'room-dining-room': ['filter.p.m.custom.filter_room', 'Dining Room'],
  'room-hallway': ['filter.p.m.custom.filter_room', 'Hallway'],
  'room-kids-room': ['filter.p.m.custom.filter_room', 'Kids Room'],
  'room-entryway': ['filter.p.m.custom.filter_room', 'Entryway'],
  'room-office': ['filter.p.m.custom.filter_room', 'Office'],
  'room-dorm': ['filter.p.m.custom.filter_room', 'Dorm Room'],
  'size-small': ['filter.p.m.custom.filter_size', 'Small'],
  'size-medium': ['filter.p.m.custom.filter_size', 'Medium'],
  'size-large': ['filter.p.m.custom.filter_size', 'Large'],
  'size-extra-large': ['filter.p.m.custom.filter_size', 'Extra Large'],
  'size-runner-small': ['filter.p.m.custom.filter_size', 'Runner - Short'],
  'size-runner-medium': ['filter.p.m.custom.filter_size', 'Runner - Long'],
  'size-runner-large': ['filter.p.m.custom.filter_size', 'Runner - Extra Long'],
  'color-beige': ['filter.p.m.custom.filter_color', 'White & Beige'],
  'color-green': ['filter.p.m.custom.filter_color', 'Green'],
  'color-blue': ['filter.p.m.custom.filter_color', 'Blue'],
  'color-red': ['filter.p.m.custom.filter_color', 'Red'],
  'color-orange': ['filter.p.m.custom.filter_color', 'Orange'],
  'color-multicolor': ['filter.p.m.custom.filter_color', 'Multicolor'],
  'color-black': ['filter.p.m.custom.filter_color', 'Black'],
  'color-gray': ['filter.p.m.custom.filter_color', 'Gray'],
  'color-brown': ['filter.p.m.custom.filter_color', 'Brown'],
  'color-purple': ['filter.p.m.custom.filter_color', 'Purple'],
  'color-neutral': ['filter.p.m.custom.filter_color', 'Neutral'],
};

class RugFinderModal extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.form = this.querySelector('[data-rug-finder-form]');
    this.steps = Array.from(this.querySelectorAll('.rug-finder__step'));
    this.progressSteps = Array.from(this.querySelectorAll('[data-rug-finder-progress] .rug-finder__progress-step'));
    this.backButton = this.querySelector('[data-rug-finder-back]');
    this.currentStep = 0;
    this.selectedTags = [];
    this.lastTrigger = null;

    this.details.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.querySelector('.rug-finder-modal__close-button').addEventListener('click', () => this.close());

    // Close on backdrop click (click landing on the overlay itself, not the dialog content).
    this.overlay = this.querySelector('.rug-finder-modal.modal-overlay');
    if (this.overlay) {
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) this.close();
      });
    }
    this.backButton.addEventListener('click', this.goBack.bind(this));

    this.form.querySelectorAll('.rug-finder__option').forEach((button) => {
      button.addEventListener('click', this.onOptionSelect.bind(this));
    });
  }

  open(trigger) {
    this.lastTrigger = trigger || null;
    this.details.setAttribute('open', true);
    document.body.classList.add('overflow-hidden');
    trapFocus(this.details.querySelector('[role="dialog"]'));
  }

  close(focusTrigger = true) {
    removeTrapFocus(focusTrigger ? this.lastTrigger : null);
    this.details.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    this.reset();
  }

  reset() {
    this.currentStep = 0;
    this.selectedTags = [];
    this.showStep(0);
  }

  onOptionSelect(event) {
    const tag = event.currentTarget.dataset.tag;
    this.selectedTags.push(tag);

    if (this.currentStep < this.steps.length - 1) {
      this.currentStep += 1;
      this.showStep(this.currentStep);
    } else {
      this.redirectToResults();
    }
  }

  goBack() {
    if (this.currentStep === 0) return;
    this.selectedTags.pop();
    this.currentStep -= 1;
    this.showStep(this.currentStep);
  }

  showStep(index) {
    this.steps.forEach((step, stepIndex) => {
      step.hidden = stepIndex !== index;
    });
    this.progressSteps.forEach((step, stepIndex) => {
      step.classList.toggle('is-active', stepIndex <= index);
    });
    this.backButton.hidden = index === 0;
  }

  buildResultsUrl(tags) {
    const params = tags
      .map((tag) => {
        const facet = RUG_FINDER_FACETS[tag];
        return facet ? `${facet[0]}=${encodeURIComponent(facet[1])}` : null;
      })
      .filter(Boolean)
      .join('&');
    return `/collections/all-rugs?${params}`;
  }

  async hasMatches(tags) {
    try {
      const response = await fetch(`${this.buildResultsUrl(tags)}&section_id=main-collection-product-grid`);
      const html = await response.text();
      return !html.includes('collection--empty');
    } catch (error) {
      return true;
    }
  }

  async redirectToResults() {
    // Room x size x color combos are frequently too narrow for this catalog's
    // size, so drop the least essential facets (color, then room) until a
    // combo actually returns products, rather than sending shoppers to a
    // dead "no products found" page.
    let tags = this.selectedTags;
    if (!(await this.hasMatches(tags))) {
      const withoutColor = tags.filter((tag) => !tag.startsWith('color-'));
      if (await this.hasMatches(withoutColor)) {
        tags = withoutColor;
      } else {
        tags = tags.filter((tag) => tag.startsWith('size-'));
      }
    }
    window.location.href = this.buildResultsUrl(tags);
  }
}

customElements.define('rug-finder-modal', RugFinderModal);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('a[href="#RugFinder-Modal"]');
  if (!trigger) return;
  event.preventDefault();
  document.querySelector('rug-finder-modal')?.open(trigger);
});
